import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Loader2, Users, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { IconPicker } from '@/components/IconPicker';
import { useAuth } from '@/contexts/AuthContext';
import { api, AdminTool } from '@/services/api';
import { getIcon } from '@/lib/icons';

interface User {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  isAdmin: boolean;
}

const DEFAULT_COLOR = '#36A9E1';

// Couleurs prédéfinies : marque Usinerie + classiques
const COLOR_SWATCHES = [
  '#36A9E1', // bleu Usinerie
  '#F5E628', // jaune Usinerie
  '#1a1a1a', // noir
  '#4285F4', // bleu Google
  '#EA4335', // rouge
  '#34A853', // vert
  '#FF8A3D', // orange
  '#7C5CFF', // violet
  '#EC4899', // rose
  '#0EA5E9', // cyan
];

const emptyForm = {
  title: '',
  description: '',
  icon: 'link',
  url: '',
  color: DEFAULT_COLOR,
  order: 0,
};

export function SettingsPage() {
  const { token } = useAuth();

  const [tools, setTools] = useState<AdminTool[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);

  const [selectedTool, setSelectedTool] = useState<AdminTool | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createForm, setCreateForm] = useState({ ...emptyForm });
  const [editForm, setEditForm] = useState({ ...emptyForm });
  const [permissionUserIds, setPermissionUserIds] = useState<number[]>([]);

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const [toolsData, usersData] = await Promise.all([
        api.tools.getAllAdmin(token),
        api.users.getAll(token),
      ]);
      setTools(toolsData.tools);
      setUsers(usersData.users);
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!token) return;
    setIsSubmitting(true);
    try {
      await api.tools.create(token, {
        title: createForm.title,
        description: createForm.description || undefined,
        icon: createForm.icon,
        url: createForm.url,
        color: createForm.color,
        order: createForm.order,
      });
      setCreateForm({ ...emptyForm });
      setCreateDialogOpen(false);
      await loadData();
    } catch (err) {
      setError('Erreur lors de la création de l\'application');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!token || !selectedTool) return;
    setIsSubmitting(true);
    try {
      await api.tools.update(token, selectedTool.id, {
        title: editForm.title,
        description: editForm.description || undefined,
        icon: editForm.icon,
        url: editForm.url,
        color: editForm.color,
        order: editForm.order,
      });
      setEditDialogOpen(false);
      setSelectedTool(null);
      await loadData();
    } catch (err) {
      setError('Erreur lors de la mise à jour de l\'application');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !selectedTool) return;
    setIsSubmitting(true);
    try {
      await api.tools.delete(token, selectedTool.id);
      setDeleteDialogOpen(false);
      setSelectedTool(null);
      await loadData();
    } catch (err) {
      setError('Erreur lors de la suppression de l\'application');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePermissions = async () => {
    if (!token || !selectedTool) return;
    setIsSubmitting(true);
    try {
      await api.tools.updatePermissions(token, selectedTool.id, permissionUserIds);
      setPermissionsDialogOpen(false);
      setSelectedTool(null);
      await loadData();
    } catch (err) {
      setError('Erreur lors de la mise à jour des accès');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (tool: AdminTool) => {
    setSelectedTool(tool);
    setEditForm({
      title: tool.title,
      description: tool.description || '',
      icon: tool.icon,
      url: tool.url,
      color: tool.color || DEFAULT_COLOR,
      order: tool.order,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (tool: AdminTool) => {
    setSelectedTool(tool);
    setDeleteDialogOpen(true);
  };

  const openPermissionsDialog = (tool: AdminTool) => {
    setSelectedTool(tool);
    setPermissionUserIds(tool.userIds);
    setPermissionsDialogOpen(true);
  };

  const getUserName = (user: User) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.email;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-background">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 text-sm text-red-300 bg-red-500/15 rounded-xl border border-red-400/30">
            {error}
          </div>
        )}

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Paramètres</h2>
            <p className="text-muted-foreground mt-1">
              Gérez les applications disponibles et leurs accès
            </p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une application
          </Button>
        </div>

        <div className="kit-glass rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Application</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Accès</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tools.map((tool) => {
                const IconComponent = getIcon(tool.icon);
                return (
                  <TableRow key={tool.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: tool.color }}
                        >
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium">{tool.title}</p>
                          {tool.description && (
                            <p className="text-xs text-muted-foreground">{tool.description}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <a
                        href={tool.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm kit-teal hover:underline flex items-center gap-1"
                      >
                        <Link className="w-3 h-3" />
                        {tool.url}
                      </a>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {tool.userIds.length === 0
                          ? 'Aucun utilisateur'
                          : `${tool.userIds.length} utilisateur${tool.userIds.length > 1 ? 's' : ''}`}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Gérer les accès"
                          onClick={() => openPermissionsDialog(tool)}
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Modifier"
                          onClick={() => openEditDialog(tool)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Supprimer"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => openDeleteDialog(tool)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {tools.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    Aucune application configurée
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Ajouter une application</DialogTitle>
            <DialogDescription>
              Configurez une nouvelle application accessible depuis up-outils.
            </DialogDescription>
          </DialogHeader>
          <ToolForm form={createForm} onChange={setCreateForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!createForm.title || !createForm.url || isSubmitting}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer l'application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Modifier l'application</DialogTitle>
            <DialogDescription>
              Modifiez les informations de cette application.
            </DialogDescription>
          </DialogHeader>
          <ToolForm form={editForm} onChange={setEditForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button
              onClick={handleEdit}
              disabled={!editForm.title || !editForm.url || isSubmitting}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Supprimer l'application</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer cette application ? Elle ne sera plus accessible pour aucun utilisateur.
            </DialogDescription>
          </DialogHeader>
          {selectedTool && (
            <div className="py-4">
              <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-xl border border-red-400/30">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: selectedTool.color }}
                >
                  {(() => {
                    const IconComponent = getIcon(selectedTool.icon);
                    return <IconComponent className="w-5 h-5 text-white" />;
                  })()}
                </div>
                <div>
                  <p className="font-medium">{selectedTool.title}</p>
                  {selectedTool.description && (
                    <p className="text-sm text-muted-foreground">{selectedTool.description}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Gérer les accès</DialogTitle>
            <DialogDescription>
              {selectedTool && (
                <>Choisissez qui peut accéder à <strong>{selectedTool.title}</strong>.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {(() => {
              const admins = users.filter(u => u.isAdmin);
              const nonAdmins = users.filter(u => !u.isAdmin);
              return (
                <div className="border rounded-md p-3 space-y-2 max-h-64 overflow-y-auto">
                  {admins.length > 0 && (
                    <>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Administrateurs</p>
                      {admins.map((user) => (
                        <div key={user.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`perm-user-${user.id}`}
                            checked={permissionUserIds.includes(user.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setPermissionUserIds([...permissionUserIds, user.id]);
                              } else {
                                setPermissionUserIds(permissionUserIds.filter(id => id !== user.id));
                              }
                            }}
                          />
                          <Label htmlFor={`perm-user-${user.id}`} className="text-sm font-normal cursor-pointer flex flex-col">
                            <span>{getUserName(user)}</span>
                            <span className="text-xs text-muted-foreground">{user.email}</span>
                          </Label>
                        </div>
                      ))}
                      {nonAdmins.length > 0 && <div className="border-t my-2" />}
                    </>
                  )}
                  {nonAdmins.length > 0 && (
                    <>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Utilisateurs</p>
                      {nonAdmins.map((user) => (
                        <div key={user.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`perm-user-${user.id}`}
                            checked={permissionUserIds.includes(user.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setPermissionUserIds([...permissionUserIds, user.id]);
                              } else {
                                setPermissionUserIds(permissionUserIds.filter(id => id !== user.id));
                              }
                            }}
                          />
                          <Label htmlFor={`perm-user-${user.id}`} className="text-sm font-normal cursor-pointer flex flex-col">
                            <span>{getUserName(user)}</span>
                            <span className="text-xs text-muted-foreground">{user.email}</span>
                          </Label>
                        </div>
                      ))}
                    </>
                  )}
                  {users.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Aucun utilisateur
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionsDialogOpen(false)} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button onClick={handleUpdatePermissions} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ToolFormProps {
  form: typeof emptyForm;
  onChange: (form: typeof emptyForm) => void;
}

function ToolForm({ form, onChange }: ToolFormProps) {
  const IconPreview = getIcon(form.icon);

  return (
    <div className="grid gap-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2 col-span-2">
          <Label htmlFor="tool-title">Nom de l'application *</Label>
          <Input
            id="tool-title"
            placeholder="Ex: Notion, Slack, Figma…"
            value={form.title}
            onChange={(e) => onChange({ ...form, title: e.target.value })}
          />
        </div>
        <div className="grid gap-2 col-span-2">
          <Label htmlFor="tool-url">URL *</Label>
          <Input
            id="tool-url"
            placeholder="https://…"
            value={form.url}
            onChange={(e) => onChange({ ...form, url: e.target.value })}
          />
        </div>
        <div className="grid gap-2 col-span-2">
          <Label htmlFor="tool-description">Description</Label>
          <Input
            id="tool-description"
            placeholder="Courte description (optionnel)"
            value={form.description}
            onChange={(e) => onChange({ ...form, description: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label>Icône</Label>
          <IconPicker value={form.icon} onChange={(icon) => onChange({ ...form, icon })} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="tool-order">Ordre d'affichage</Label>
          <Input
            id="tool-order"
            type="number"
            min={0}
            value={form.order}
            onChange={(e) => onChange({ ...form, order: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div className="grid gap-2 col-span-2">
          <Label>Couleur du pictogramme</Label>
          <div className="flex flex-wrap items-center gap-2">
            {COLOR_SWATCHES.map((swatch) => (
              <button
                key={swatch}
                type="button"
                aria-label={`Couleur ${swatch}`}
                onClick={() => onChange({ ...form, color: swatch })}
                className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                  form.color.toLowerCase() === swatch.toLowerCase()
                    ? 'ring-2 ring-offset-2 ring-foreground'
                    : 'ring-1 ring-black/10'
                }`}
                style={{ backgroundColor: swatch }}
              />
            ))}
            <div className="flex items-center gap-2 ml-1">
              <input
                type="color"
                aria-label="Couleur personnalisée"
                value={form.color}
                onChange={(e) => onChange({ ...form, color: e.target.value })}
                className="w-8 h-8 rounded cursor-pointer border border-input bg-transparent p-0.5"
              />
              <Input
                value={form.color}
                onChange={(e) => onChange({ ...form, color: e.target.value })}
                className="w-28 font-mono text-sm uppercase"
              />
            </div>
          </div>
        </div>
        <div className="grid gap-2 col-span-2">
          <Label>Aperçu</Label>
          <div className="flex items-center gap-2">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: form.color }}
            >
              <IconPreview className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm text-muted-foreground">{form.title || 'Nom'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
